import React, { useMemo, useState } from 'react';
import { Alert, Button, Checkbox, Empty, Input, Modal, Popconfirm, Select, Space, Spin, Table, Tag, Typography, message } from 'antd';
import PageTabs from '../page/PageTabs';
import { CopyOutlined, PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import { apiConfig } from '../../authConfig';
import { Role } from '../../constants/roles';
import { useApiToken } from '../../hooks/useApiToken';
import { useResource } from '../../hooks/useResource';
import { createApiKey, listApiKeyScopes, listApiKeys, revokeApiKey, rotateApiKey } from '../../utils/integrationsApi';
import { formatDdMmYyyy, formatDdMmYyyyTime } from '../../utils/formatDdMmYyyy';
import ApiDocs from './ApiDocs';
import { copyText } from './copyText';
import '../../styles/integrations.css';

const { Text, Paragraph, Title } = Typography;

const EXPIRY_OPTIONS = [30, 90, 180, 365].map((d) => ({ value: d, label: `${d} days` }));
const DEFAULT_EXPIRY_DAYS = 90;

/** Lifecycle label from the server's own timestamps (display only; the API decides what authenticates). */
export function keyState(k, now = Date.now()) {
    if (k.revokedAt) return { label: 'Revoked', tone: 'default' };
    if (k.supersededAt) {
        return new Date(k.supersededAt).getTime() > now
            ? { label: `Rotating, old key valid until ${formatDdMmYyyyTime(k.supersededAt)}`, tone: 'orange' }
            : { label: 'Superseded', tone: 'default' };
    }
    if (k.expiresAt && new Date(k.expiresAt).getTime() <= now) return { label: 'Expired', tone: 'red' };
    return { label: 'Active', tone: 'green' };
}
const isLive = (k) => !k.revokedAt && !(k.supersededAt && new Date(k.supersededAt).getTime() <= Date.now());

/** The one place a plaintext secret exists. It lives only in this modal's state and is dropped on close. */
const SecretModal = ({ reveal, onClose }) => (
    <Modal
        open={!!reveal}
        title={reveal?.rotated ? 'New key after rotation' : 'API key created'}
        onCancel={onClose}
        maskClosable={false}
        closable={false}
        footer={<Button type="primary" onClick={onClose}>I have copied it</Button>}
    >
        {reveal && (
            <div>
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 12 }}
                    message="You will not see this key again"
                    description="Copy it now and store it in your secret manager. Only the prefix is kept visible afterwards; if you lose it, rotate or create a new key."
                />
                <Text strong>{reveal.name}</Text>
                <code className="int-code" data-testid="api-key-secret" style={{ margin: '8px 0' }}>{reveal.secret}</code>
                <Button icon={<CopyOutlined />} onClick={() => copyText(reveal.secret)}>Copy key</Button>
            </div>
        )}
    </Modal>
);

const parseIps = (text) => text.split(/[\s,]+/).map((s) => s.trim()).filter(Boolean);

const CreateKeyModal = ({ open, scopes, onCancel, onCreate }) => {
    const readScopes = useMemo(() => scopes.filter((s) => s.access === 'read').map((s) => s.scope), [scopes]);
    const [name, setName] = useState('');
    const [picked, setPicked] = useState(null); // null = untouched -> read scopes default
    const [days, setDays] = useState(DEFAULT_EXPIRY_DAYS);
    const [ips, setIps] = useState('');
    const [rate, setRate] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);

    const selected = picked ?? readScopes;
    const byResource = useMemo(() => {
        const g = {};
        for (const s of scopes) (g[s.resource] = g[s.resource] || []).push(s);
        return g;
    }, [scopes]);
    const rateNum = rate.trim() === '' ? undefined : Number(rate);
    const rateOk = rateNum === undefined || (Number.isInteger(rateNum) && rateNum > 0);
    const canSubmit = name.trim().length > 0 && selected.length > 0 && rateOk && !saving;

    const toggle = (scope, on) => setPicked(on ? [...selected, scope] : selected.filter((s) => s !== scope));

    const submit = async () => {
        setSaving(true);
        setError(null);
        try {
            await onCreate({ name: name.trim(), scopes: selected, expiresInDays: days, ipAllowlist: parseIps(ips), rateLimitPerMinute: rateNum });
        } catch (err) {
            setError(err.message || 'Could not create the key.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Modal
            open={open}
            title="Create Open API key"
            onCancel={onCancel}
            destroyOnClose
            footer={(
                <Space>
                    <Button onClick={onCancel}>Cancel</Button>
                    <Button type="primary" disabled={!canSubmit} loading={saving} onClick={submit}>Create key</Button>
                </Space>
            )}
        >
            <div style={{ display: 'grid', gap: 14 }}>
                <div>
                    <Text strong>Name</Text>
                    <Input aria-label="Key name" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Reporting export" />
                </div>
                <div>
                    <Text strong>Scopes</Text>
                    <Paragraph type="secondary" style={{ marginBottom: 8 }}>Read scopes are pre-selected. Write scopes change data and are off until you tick them.</Paragraph>
                    {Object.entries(byResource).map(([resource, list]) => (
                        <div key={resource} style={{ marginBottom: 8 }}>
                            <Text type="secondary" style={{ textTransform: 'capitalize' }}>{resource}</Text>
                            {list.map((s) => (
                                <div key={s.scope}>
                                    <Checkbox checked={selected.includes(s.scope)} onChange={(e) => toggle(s.scope, e.target.checked)}>
                                        <Text code>{s.scope}</Text>{' '}
                                        {s.access === 'write' ? <Tag color="orange">write</Tag> : <Tag>read</Tag>}
                                        <Text type="secondary"> {s.description}</Text>
                                    </Checkbox>
                                </div>
                            ))}
                        </div>
                    ))}
                </div>
                <div>
                    <Text strong>Expires after</Text>
                    <Select aria-label="Expiry" style={{ width: '100%' }} value={days} onChange={setDays} options={EXPIRY_OPTIONS} />
                </div>
                <div>
                    <Text strong>IP allowlist (optional)</Text>
                    <Input.TextArea aria-label="IP allowlist" rows={2} value={ips} onChange={(e) => setIps(e.target.value)} placeholder="Comma or newline separated IPs / CIDRs. Empty allows any address." />
                </div>
                <div>
                    <Text strong>Rate limit per minute (optional)</Text>
                    <Input aria-label="Rate limit per minute" inputMode="numeric" value={rate} onChange={(e) => setRate(e.target.value)} placeholder="Leave blank for the server default" />
                    {!rateOk && <Text type="danger">Enter a whole number greater than zero.</Text>}
                </div>
                {error && <Alert type="error" showIcon message="Could not create the key" description={error} />}
            </div>
        </Modal>
    );
};

const RotateModal = ({ target, onCancel, onRotate }) => {
    const [hours, setHours] = useState('');
    const [saving, setSaving] = useState(false);
    const num = hours.trim() === '' ? undefined : Number(hours);
    const ok = num === undefined || (Number.isInteger(num) && num >= 0);
    const go = async () => {
        setSaving(true);
        try {
            await onRotate(target, num);
        } catch (err) {
            message.error(err.message || 'Could not rotate the key.');
        } finally {
            setSaving(false);
        }
    };
    return (
        <Modal
            open={!!target}
            title={target ? `Rotate ${target.name}` : ''}
            onCancel={onCancel}
            destroyOnClose
            footer={<Space><Button onClick={onCancel}>Cancel</Button><Button type="primary" disabled={!ok} loading={saving} onClick={go}>Rotate key</Button></Space>}
        >
            <Paragraph>A new key with the same scopes is issued and shown once. The current key keeps working for the overlap window so you can deploy the new one without downtime.</Paragraph>
            <Text strong>Overlap hours (optional)</Text>
            <Input aria-label="Overlap hours" inputMode="numeric" value={hours} onChange={(e) => setHours(e.target.value)} placeholder="Leave blank for the server default" />
            {!ok && <Text type="danger">Enter a whole number of hours.</Text>}
        </Modal>
    );
};

/**
 * Open API keys. Lead Advisor: list, create, rotate, revoke. Platform admin: list and revoke the tenant's keys only
 * (a key is minted by the person who will use it). The secret is returned once by the server and shown once here.
 */
const KeysView = ({ role, onOpenDocs }) => {
    const getToken = useApiToken();
    const canManage = role === Role.LEAD_ADVISOR;
    const keys = useResource(() => listApiKeys(getToken), [getToken]);
    const scopes = useResource(() => listApiKeyScopes(getToken), [getToken], { enabled: canManage });
    const [creating, setCreating] = useState(false);
    const [rotating, setRotating] = useState(null);
    const [reveal, setReveal] = useState(null);

    const base = `${apiConfig.baseUrl}/api/v1/open/v1`;
    const specUrl = `${base}/openapi.json`;
    const curl = `curl -H "Authorization: Bearer plk_live_YOUR_KEY" \\\n  ${specUrl}`;

    const revoke = async (k) => {
        try {
            await revokeApiKey(getToken, k.id);
            message.success(`${k.name} revoked.`);
        } catch (err) {
            message.error(err.message || 'Could not revoke the key.');
        }
        await keys.reload();
    };

    const columns = [
        { title: 'Name', dataIndex: 'name' },
        { title: 'Key', dataIndex: 'keyPrefix', render: (v) => <Text code>{v}…</Text> },
        { title: 'Scopes', dataIndex: 'scopes', render: (v) => (v || []).map((s) => <Tag key={s}>{s}</Tag>) },
        {
            title: 'Status',
            render: (_, k) => {
                const st = keyState(k);
                return <Tag color={st.tone === 'default' ? undefined : st.tone}>{st.label}</Tag>;
            },
        },
        { title: 'Expires', dataIndex: 'expiresAt', render: (v) => (v ? formatDdMmYyyy(v) : 'Never') },
        {
            title: 'Last used',
            render: (_, k) => (k.lastUsedAt ? `${formatDdMmYyyyTime(k.lastUsedAt)}${k.lastUsedIp ? ` from ${k.lastUsedIp}` : ''}` : 'Never used'),
        },
        { title: 'Rate / min', dataIndex: 'rateLimitPerMinute', render: (v) => v ?? 'Default' },
        {
            title: 'Actions',
            render: (_, k) => (isLive(k) ? (
                <Space>
                    {canManage && <Button size="small" onClick={() => setRotating(k)}>Rotate</Button>}
                    <Popconfirm
                        title={`Revoke ${k.name}?`}
                        description="Anything using this key stops working immediately. This cannot be undone."
                        okText="Revoke"
                        okButtonProps={{ danger: true }}
                        onConfirm={() => revoke(k)}
                    >
                        <Button size="small" danger>Revoke</Button>
                    </Popconfirm>
                </Space>
            ) : null),
        },
    ];

    return (
        <div>
            <div className="int-panel int-two-col" style={{ marginBottom: 24 }}>
                <div style={{ display: 'grid', gap: 10, alignContent: 'start' }}>
                    <div className="int-panel-head">
                        <Title level={5} style={{ margin: 0 }}>Using the Open API</Title>
                        <Button type="primary" size="small" onClick={onOpenDocs}>API documentation</Button>
                    </div>
                    <div><Text strong>Base URL </Text><Text code copyable>{base}</Text></div>
                    <div><Text strong>OpenAPI spec </Text><a href={specUrl} target="_blank" rel="noreferrer">{specUrl}</a></div>
                    <code className="int-code">{curl}</code>
                    <div><Button size="small" icon={<CopyOutlined />} onClick={() => copyText(curl)}>Copy example</Button></div>
                </div>
                <div className="int-note">
                    <Text strong>What a key can and cannot do</Text>
                    <ul>
                        <li>Scoped to your firm: it only ever sees your firm's data.</li>
                        <li>It can never approve or publish anything; approvals and publishing always need a signed-in human.</li>
                        <li>Shown once when created or rotated; afterwards only its prefix is visible.</li>
                    </ul>
                </div>
            </div>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
                <Title level={5} style={{ margin: 0 }}>{canManage ? 'Your API keys' : 'API keys in this firm'}</Title>
                {canManage && (
                    <Button type="primary" icon={<PlusOutlined />} disabled={scopes.status !== 'ready'} onClick={() => setCreating(true)}>Create key</Button>
                )}
            </div>
            {!canManage && (
                <Alert type="info" showIcon style={{ marginBottom: 12 }} message="Platform admins can review and revoke keys. Keys are created and rotated by the Lead Advisor who owns them." />
            )}
            {canManage && scopes.status === 'error' && (
                <Alert type="error" showIcon style={{ marginBottom: 12 }} message="Could not load the available scopes" description={scopes.error.message}
                    action={<Button size="small" icon={<ReloadOutlined />} onClick={scopes.reload}>Retry</Button>} />
            )}

            {(keys.status === 'loading' || keys.status === 'idle') && !keys.data && <div style={{ textAlign: 'center', padding: 32 }}><Spin /></div>}
            {keys.status === 'error' && (
                <Alert type="error" showIcon message="Could not load API keys" description={keys.error.message}
                    action={<Button size="small" icon={<ReloadOutlined />} onClick={keys.reload}>Retry</Button>} />
            )}
            {keys.status !== 'error' && keys.data && keys.data.length === 0 && (
                <Empty description={canManage ? 'No API keys yet. Create one to call the Open API.' : 'No API keys have been created in this firm.'} />
            )}
            {keys.status !== 'error' && keys.data && keys.data.length > 0 && (
                <Table rowKey={(r) => r.id} size="middle" columns={columns} dataSource={keys.data} pagination={false} scroll={{ x: true }} />
            )}

            {canManage && scopes.status === 'ready' && (
                <CreateKeyModal
                    open={creating}
                    scopes={scopes.data}
                    onCancel={() => setCreating(false)}
                    onCreate={async (input) => {
                        const res = await createApiKey(getToken, input);
                        setCreating(false);
                        setReveal({ name: res.key?.name || input.name, secret: res.secret, rotated: false });
                        await keys.reload();
                    }}
                />
            )}
            <RotateModal
                target={rotating}
                onCancel={() => setRotating(null)}
                onRotate={async (k, overlapHours) => {
                    const res = await rotateApiKey(getToken, k.id, { overlapHours });
                    setRotating(null);
                    setReveal({ name: res.key?.name || k.name, secret: res.secret, rotated: true });
                    await keys.reload();
                }}
            />
            <SecretModal reveal={reveal} onClose={() => setReveal(null)} />
        </div>
    );
};

const OpenApiTab = ({ role }) => {
    const [view, setView] = useState('keys');
    return (
        <PageTabs
            activeKey={view}
            onChange={setView}
            items={[
                { key: 'keys', label: 'Keys', children: <KeysView role={role} onOpenDocs={() => setView('docs')} /> },
                { key: 'docs', label: 'Documentation', children: <ApiDocs /> },
            ]}
        />
    );
};

export default OpenApiTab;
